<template>
  <q-dialog :model-value="true" :persistent="submitting" @hide="$emit('close')">
    <q-card style="width: 500px; max-width: 95vw">
      <q-form greedy @submit="submit">
        <q-card-section>
          <div class="text-h6">Request a missing property</div>
          <p class="q-mt-sm q-mb-none">
            Share the property's website and address so we can try to add it to HelloData.
          </p>
        </q-card-section>
        <q-card-section class="q-gutter-y-md">
          <q-input
            v-model="website"
            label="Property website"
            placeholder="https://www.example.com"
            :rules="[websiteRule]"
            :disable="submitting"
            maxlength="2048"
            autocomplete="url"
            autofocus
            outlined
          />
          <address-google-autocomplete
            ref="addressInput"
            :value="address"
            label="Full property address"
            hint="Choose a Google suggestion or type the full address, including city, state, and ZIP code."
            :rules="[addressRule]"
            :disable="submitting"
            :filled="false"
            maxlength="1000"
            outlined
            @input="addressEdited"
            @click="addressSelected"
            @keydown.enter.prevent
          />
          <div v-if="error" role="alert" class="text-negative">{{ error }}</div>
        </q-card-section>
        <q-card-actions align="right">
          <q-btn label="Cancel" flat no-caps :disable="submitting" @click="$emit('close')" />
          <q-btn
            label="Submit request"
            type="submit"
            color="primary"
            no-caps
            unelevated
            :loading="submitting"
            :disable="submitting"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { QInput } from 'quasar'
import { normalizePropertyRequestWebsite, propertyRequestAddressError } from 'common'
import propertyApi from '@/api/property'
import AddressGoogleAutocomplete from './AddressGoogleAutocomplete.vue'

export default defineComponent({
  name: 'PropertyRequestDialog',
  components: { AddressGoogleAutocomplete },
  props: {
    searchText: {
      type: String,
      required: true
    }
  },
  emits: ['close'],
  data() {
    return {
      website: '',
      address: '',
      submitting: false,
      error: ''
    }
  },
  methods: {
    websiteRule(value: string): true | string {
      try {
        normalizePropertyRequestWebsite(value)
        return true
      } catch (error) {
        return (error as Error).message
      }
    },
    addressRule(): true | string {
      return propertyRequestAddressError(this.address) || true
    },
    addressEdited(value: string | number | null) {
      this.address = typeof value === 'string' ? value : ''
    },
    addressSelected(place: google.maps.places.PlaceResult) {
      if (this.submitting) {
        return
      }
      if (place.formatted_address) {
        this.address = place.formatted_address.trim()
      }
      const addressInput = this.$refs.addressInput as Pick<QInput, 'validate'> | undefined
      void addressInput?.validate()
    },
    async submit() {
      if (this.submitting) {
        return
      }
      if (this.addressRule() !== true) {
        const addressInput = this.$refs.addressInput as Pick<QInput, 'validate'> | undefined
        void addressInput?.validate()
        return
      }
      this.error = ''
      this.submitting = true
      try {
        await propertyApi.submitPropertyRequest({
          website_url: normalizePropertyRequestWebsite(this.website),
          property_address: this.address.trim(),
          search_text: this.searchText.trim().slice(0, 1000) || undefined
        })
        this.$q.notify({
          type: 'positive',
          message: 'Request received. We’ll try to add this property to HelloData.'
        })
        this.$emit('close')
      } catch {
        this.error = 'We couldn’t save your request. Please try again.'
      } finally {
        this.submitting = false
      }
    }
  }
})
</script>
